// src/pages/Register.tsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { register, clearError } from "../redux/slices/authSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Shield,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    password: "",
    password_confirmation: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, user, token, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );

  useEffect(() => {
    if (token && user && !user.email_verified_at) {
      navigate("/verify-email", { state: { email: user.email } });
      toast.success("Registration successful! Please verify your email.");
    }
  }, [token, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.email_verified_at) {
      navigate("/dashboard");
      toast.success("Registration complete! Welcome to Pelivor Recovery.");
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === "string" ? error : "Registration failed");
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const validatePassword = (password: string) => {
    const errors: string[] = [];
    const requirements = [
      { test: password.length >= 8, message: "At least 8 characters" },
      { test: /[A-Z]/.test(password), message: "One uppercase letter" },
      { test: /[a-z]/.test(password), message: "One lowercase letter" },
      { test: /\d/.test(password), message: "One number" },
      { test: /[!@#$%^&*]/.test(password), message: "One special character" },
    ];

    requirements.forEach((req) => {
      if (!req.test) errors.push(req.message);
    });

    setPasswordErrors(errors);
    setPasswordStrength(Math.floor(((5 - errors.length) / 5) * 100));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      validatePassword(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.first_name ||
      !formData.last_name ||
      !formData.username ||
      !formData.email ||
      !formData.password ||
      !formData.password_confirmation
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      toast.error("Passwords do not match");
      return;
    }

    if (passwordStrength < 60) {
      toast.error("Please use a stronger password (at least 60% strength)");
      return;
    }

    if (!termsAccepted) {
      toast.error("Please accept the Terms of Service and Privacy Policy");
      return;
    }

    const result = await dispatch(register(formData) as any);

    if (register.fulfilled.match(result)) {
      console.log("Registration successful, awaiting email verification");
    }
  };

  const getStrengthColor = (strength: number) => {
    if (strength < 40) return "bg-rose-500";
    if (strength < 80) return "bg-amber-500";
    return "bg-emerald-500";
  };

  const getStrengthText = (strength: number) => {
    if (strength < 40) return "Weak";
    if (strength < 80) return "Medium";
    return "Strong";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header with Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <img src={logo} alt="Pelivor Recovery" className="h-12 w-auto" />
              <div className="absolute -top-1 -right-1 h-3 w-3 bg-emerald-400 rounded-full animate-pulse border-2 border-white dark:border-gray-900"></div>
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Create Account
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Join the secure crypto recovery platform
            </p>
          </div>
        </div>

        {/* Registration Form */}
        <div className={`${GlobalStyles.card} !p-8`}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="first_name"
                  className={`${GlobalStyles.inputLabel} flex items-center gap-2`}
                >
                  <User className="h-4 w-4 text-primary" />
                  <span>First Name</span>
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={handleChange}
                  className={GlobalStyles.input}
                  placeholder="John"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="last_name"
                  className={`${GlobalStyles.inputLabel} flex items-center gap-2`}
                >
                  <User className="h-4 w-4 text-primary" />
                  <span>Last Name</span>
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={handleChange}
                  className={GlobalStyles.input}
                  placeholder="Doe"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Username */}
            <div className="space-y-2">
              <label
                htmlFor="username"
                className={`${GlobalStyles.inputLabel} flex items-center gap-2`}
              >
                <User className="h-4 w-4 text-primary" />
                <span>Username</span>
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleChange}
                className={GlobalStyles.input}
                placeholder="johndoe"
                disabled={isLoading}
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label
                htmlFor="email"
                className={`${GlobalStyles.inputLabel} flex items-center gap-2`}
              >
                <Mail className="h-4 w-4 text-primary" />
                <span>Email Address</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={GlobalStyles.input}
                placeholder="you@example.com"
                disabled={isLoading}
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label
                htmlFor="password"
                className={`${GlobalStyles.inputLabel} flex items-center gap-2`}
              >
                <Lock className="h-4 w-4 text-primary" />
                <span>Password</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={GlobalStyles.input}
                  placeholder="Create a strong password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>

              {/* Password Strength */}
              {formData.password && (
                <div className="space-y-3 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Password Strength:{" "}
                      <span
                        className={`font-bold ${getStrengthColor(passwordStrength).replace("bg-", "text-")}`}
                      >
                        {getStrengthText(passwordStrength)}
                      </span>
                    </span>
                    <span className="text-sm font-bold text-primary">
                      {passwordStrength}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${getStrengthColor(passwordStrength)} transition-all duration-500`}
                      style={{ width: `${passwordStrength}%` }}
                    ></div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      "8+ characters",
                      "Uppercase letter",
                      "Lowercase letter",
                      "Number",
                      "Special character",
                    ].map((req) => {
                      const isMet = !passwordErrors.includes(
                        req.split(" ")[1] === "+"
                          ? "At least 8 characters"
                          : req.includes("Uppercase")
                            ? "One uppercase letter"
                            : req.includes("Lowercase")
                              ? "One lowercase letter"
                              : req.includes("Number")
                                ? "One number"
                                : "One special character",
                      );
                      return (
                        <div key={req} className="flex items-center text-xs">
                          {isMet ? (
                            <CheckCircle className="h-3 w-3 text-emerald-500 mr-1.5 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-3 w-3 text-rose-500 mr-1.5 flex-shrink-0" />
                          )}
                          <span
                            className={
                              isMet
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-gray-500"
                            }
                          >
                            {req}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label
                htmlFor="password_confirmation"
                className={`${GlobalStyles.inputLabel}`}
              >
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="password_confirmation"
                  name="password_confirmation"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  className={`${GlobalStyles.input} ${
                    formData.password_confirmation &&
                    formData.password !== formData.password_confirmation
                      ? "border-rose-500"
                      : formData.password_confirmation &&
                          formData.password === formData.password_confirmation
                        ? "border-emerald-500"
                        : ""
                  }`}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {formData.password_confirmation &&
                formData.password !== formData.password_confirmation && (
                  <p className="text-sm text-rose-500">
                    Passwords do not match
                  </p>
                )}
            </div>

            {/* Terms Agreement */}
            <div className="flex items-start pt-2">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mt-0.5 flex-shrink-0"
                disabled={isLoading}
              />
              <label
                htmlFor="terms"
                className="ml-2 text-sm text-gray-600 dark:text-gray-400"
              >
                I agree to the{" "}
                <Link
                  to="/terms"
                  className="text-primary hover:text-primary/80 font-medium"
                  target="_blank"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  className="text-primary hover:text-primary/80 font-medium"
                  target="_blank"
                >
                  Privacy Policy
                </Link>
              </label>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading || !termsAccepted}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} w-full group/register ${
                  !termsAccepted ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div
                      className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                    ></div>
                    Creating account...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <span>Create Account</span>
                    <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover/register:translate-x-1" />
                  </div>
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                Already have an account?
              </span>
            </div>
          </div>

          {/* Login Link */}
          <Link
            to="/login"
            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full group/login`}
          >
            <span>Sign In Instead</span>
            <Sparkles className="h-4 w-4 ml-2 opacity-0 group-hover/login:opacity-100 transition-opacity" />
          </Link>

          {/* Security Info */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Shield className="h-4 w-4 text-emerald-500" />
              <span>Enterprise-grade security & encryption</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
